import React, { useState, useEffect, useCallback } from 'react';
import { ShieldAlert, CheckCircle, Eye, X, RefreshCw } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const severityConfig = {
  low: { color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.2)', label: 'Low' },
  medium: { color: '#F97316', bg: 'rgba(249,115,22,0.1)', border: 'rgba(249,115,22,0.2)', label: 'Medium' },
  high: { color: '#EF4444', bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.2)', label: 'High' },
  critical: { color: '#FF0040', bg: 'rgba(255,0,64,0.12)', border: 'rgba(255,0,64,0.35)', label: 'Critical' },
};

const statusConfig = {
  open: { color: '#EF4444', bg: 'rgba(239,68,68,0.1)', label: 'Open' },
  investigating: { color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', label: 'Investigating' },
  resolved: { color: '#10B981', bg: 'rgba(16,185,129,0.1)', label: 'Resolved' },
};

function AlertDetailModal({ alert, onClose, onAction }) {
  if (!alert) return null;
  const sev = severityConfig?.[alert?.severity] || severityConfig?.medium;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-lg rounded-2xl p-6" style={{ background: '#0D1B2A', border: `1px solid ${sev?.border}` }}>
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <ShieldAlert size={18} style={{ color: sev?.color }} />
            <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Alert Details</h3>
          </div>
          <button onClick={onClose} style={{ color: '#64748B' }}><X size={18} /></button>
        </div>
        <div className="space-y-3 mb-5">
          <div className="rounded-xl p-4" style={{ background: `${sev?.bg}`, border: `1px solid ${sev?.border}` }}>
            <p className="text-sm font-medium text-white mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>{alert?.event_type?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase())}</p>
            <p className="text-xs" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{alert?.description}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[{ label: 'IP Address', value: alert?.ip_address || 'N/A' },
              { label: 'Device', value: alert?.device_info || 'N/A' },
              { label: 'Severity', value: sev?.label },
              { label: 'Status', value: statusConfig?.[alert?.status]?.label || alert?.status }]?.map(item => (
              <div key={item?.label} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.08)' }}>
                <p className="text-xs mb-1" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{item?.label}</p>
                <p className="text-sm font-medium text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{item?.value}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {alert?.status === 'open' && (
            <button onClick={() => onAction('investigating', alert)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium" style={{ background: 'rgba(245,158,11,0.1)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.2)', fontFamily: 'DM Sans, sans-serif' }}>
              <Eye size={12} /> Mark Investigating
            </button>
          )}
          {alert?.status !== 'resolved' && (
            <button onClick={() => onAction('resolved', alert)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium" style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981', border: '1px solid rgba(16,185,129,0.2)', fontFamily: 'DM Sans, sans-serif' }}>
              <CheckCircle size={12} /> Resolve
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FraudMonitoring() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [actionMsg, setActionMsg] = useState('');

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase?.from('fraud_alerts')?.select('*');
      if (filterStatus !== 'all') query = query?.eq('status', filterStatus);
      const { data, error } = await query?.order('created_at', { ascending: false });
      if (!error) setAlerts(data || []);
    } catch (err) {
      console.error('Load alerts error:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  // Real-time subscription for new fraud alerts
  useEffect(() => {
    const channel = supabase?.channel('admin_fraud_realtime')
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'fraud_alerts' }, () => {
        loadAlerts();
      })
      ?.subscribe();
    return () => supabase?.removeChannel(channel);
  }, [loadAlerts]);

  const handleAction = async (newStatus, alert) => {
    setSelectedAlert(null);
    try {
      await supabase?.from('fraud_alerts')?.update({ status: newStatus, updated_at: new Date()?.toISOString() })?.eq('id', alert?.id);
      setActionMsg(`Alert marked as ${newStatus}.`);
      loadAlerts();
      setTimeout(() => setActionMsg(''), 3000);
    } catch (err) {
      console.error('Action error:', err);
    }
  };

  const openCount = alerts?.filter(a => a?.status === 'open')?.length || 0;
  const criticalCount = alerts?.filter(a => a?.severity === 'critical' && a?.status !== 'resolved')?.length || 0;
  const resolvedToday = alerts?.filter(a => {
    const today = new Date()?.toISOString()?.split('T')?.[0];
    return a?.status === 'resolved' && a?.updated_at?.startsWith(today);
  })?.length || 0;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Fraud Monitoring</h2>
        <button onClick={loadAlerts} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs" style={{ background: 'rgba(0,229,255,0.08)', color: '#00E5FF', border: '1px solid rgba(0,229,255,0.2)', fontFamily: 'DM Sans, sans-serif' }}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {actionMsg && (
        <div className="px-4 py-2.5 rounded-xl text-sm" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', color: '#10B981', fontFamily: 'DM Sans, sans-serif' }}>{actionMsg}</div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        {[{ label: 'Open Alerts', value: openCount, color: '#EF4444' },
          { label: 'Critical Unresolved', value: criticalCount, color: '#FF0040' },
          { label: 'Resolved Today', value: resolvedToday, color: '#10B981' }]?.map(item => (
          <div key={item?.label} className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${item?.color}25` }}>
            <p className="text-2xl font-bold mb-1" style={{ color: item?.color, fontFamily: 'DM Sans, sans-serif' }}>{item?.value}</p>
            <p className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{item?.label}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['all', 'open', 'investigating', 'resolved']?.map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-all"
            style={{ background: filterStatus === s ? 'rgba(0,229,255,0.12)' : 'rgba(0,0,0,0.3)', color: filterStatus === s ? '#00E5FF' : '#64748B', border: filterStatus === s ? '1px solid rgba(0,229,255,0.2)' : '1px solid rgba(0,229,255,0.08)', fontFamily: 'DM Sans, sans-serif' }}>
            {s === 'all' ? 'All' : s?.charAt(0)?.toUpperCase() + s?.slice(1)}
          </button>
        ))}
      </div>

      {/* Alerts Table */}
      <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,229,255,0.1)' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ background: 'rgba(0,0,0,0.4)' }}>
                {['Event Type', 'Severity', 'Status', 'Timestamp', 'Actions']?.map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Loading alerts...</td></tr>
              ) : alerts?.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>No alerts found</td></tr>
              ) : alerts?.map((alert, i) => {
                const sev = severityConfig?.[alert?.severity] || severityConfig?.medium;
                const stat = statusConfig?.[alert?.status] || statusConfig?.open;
                return (
                  <tr key={alert?.id} style={{ borderTop: '1px solid rgba(0,229,255,0.06)', background: alert?.severity === 'critical' ? 'rgba(255,0,64,0.03)' : i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                    <td className="px-4 py-3">
                      <p className="text-sm text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{alert?.event_type?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c?.toUpperCase())}</p>
                      <p className="text-xs truncate max-w-[200px]" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{alert?.description}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ background: sev?.bg, color: sev?.color, border: `1px solid ${sev?.border}`, fontFamily: 'DM Sans, sans-serif' }}>{sev?.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-1 rounded-full" style={{ background: stat?.bg, color: stat?.color, fontFamily: 'DM Sans, sans-serif' }}>{stat?.label}</span>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{alert?.created_at ? new Date(alert?.created_at)?.toLocaleString() : 'N/A'}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => setSelectedAlert(alert)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs" style={{ background: 'rgba(0,229,255,0.08)', color: '#00E5FF', border: '1px solid rgba(0,229,255,0.15)', fontFamily: 'DM Sans, sans-serif' }}>
                        <Eye size={12} /> View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selectedAlert && <AlertDetailModal alert={selectedAlert} onClose={() => setSelectedAlert(null)} onAction={handleAction} />}
    </div>
  );
}
